import { ApiError, apiGet, apiPost, storeSession } from './client';

/* ---------------------------------------------------------------------------
   Every endpoint the landing site talks to.

   Each payload below was checked against the live handler in
   team-grid-node-app/server (routes/auth.js, routes/contact.js) — not against
   what the old landing pages happened to send. Where the two disagreed, the
   server won. Notes on each call record what the server actually enforces, so
   a future edit doesn't silently reintroduce a rejected shape.
   --------------------------------------------------------------------------- */

/** Browser timezone, for the admin notification email. Never throws. */
function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/* ------------------------------- auth ---------------------------------- */

/* POST /auth/login — validateLogin only, NO reCAPTCHA middleware.
   Sends recaptchaToken when present purely for parity with the host. */
export const login = async ({ email, password, rememberMe = true, recaptchaToken }) => {
  /* parity with the host's AuthContext.login: clear any prior session first */
  try {
    ['token', 'refreshToken', 'organization', 'isMainAdminSwitch', 'mainAdminSwitchUserId']
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* private mode */ }
  const r = await apiPost('/api/v1/auth/login', {
    email: email.trim(),
    password,
    rememberMe,
    ...(recaptchaToken ? { recaptchaToken } : {}),
  });
  /* No token means no session — don't redirect into the app as if signed in. */
  if (!storeSession(r)) {
    throw new ApiError('Sign-in did not return a session. Please try again.', 0, r);
  }
  return r;
};

/* POST /auth/register — verifyRecaptcha THEN validateRegistration.
   The server requires firstName AND lastName at 2–50 chars each, so a
   single-word name is rejected; RegisterPage validates for that before
   we get here. organizationName is what creates the workspace (the field is
   named `companyName` in the UI). Invitation signups instead pass
   organizationId + invitationToken + isInvitationSignup and skip org creation. */
export const register = async ({
  name,
  email,
  password,
  companyName,
  recaptchaToken,
  invitation,
}) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';

  const body = {
    firstName,
    lastName,
    email: email.trim(),
    password,
    recaptchaToken,
  };

  if (invitation?.token && invitation?.organizationId) {
    body.isInvitationSignup = true;
    body.organizationId = invitation.organizationId;
    body.invitationToken = invitation.token;
  } else {
    body.organizationName = companyName?.trim();
  }

  const r = await apiPost('/api/v1/auth/register', body);
  /* The plan step needs a real user id to check out with. If the response came
     back without one the account doesn't exist, and showing plan selection
     anyway would send someone into a payment flow for nothing. */
  if (!r?.data?.user?.id) {
    throw new ApiError('Your account could not be created. Please try again or contact support.', 0, r);
  }
  storeSession(r);
  return r;
};

/* POST /auth/google — one endpoint for both directions, switched by isSignup.
   `idToken` is the Google ID token (the `credential` from the Google button),
   not an access token. Signing UP additionally needs organizationName, because
   that call creates the workspace. Same contract as the host's AuthContext
   googleLogin() and Register.js handleGoogleSignup(). */
export const googleAuth = async ({ idToken, isSignup = false, organizationName }) => {
  const r = await apiPost('/api/v1/auth/google', {
    idToken,
    isSignup,
    ...(isSignup && organizationName ? { organizationName: organizationName.trim() } : {}),
  });
  /* Signing up hands off to plan selection, which needs the tokens kept for the
     free-plan path — storing them here matches what the host does for sign-in
     and is harmless for sign-up. */
  if (!storeSession(r)) {
    throw new ApiError('Google sign-in did not return a session. Please try again.', 0, r);
  }
  return r;
};

export const forgotPassword = ({ email }) =>
  apiPost('/api/v1/auth/forgot-password', { email: email.trim() });

/* POST /auth/reset-password — body("token"), body("password").isLength({min:8}).
   The server reads exactly { token, password }; there is no `newPassword`. */
export const resetPassword = ({ token, password }) =>
  apiPost('/api/v1/auth/reset-password', { token, password });

/* POST /auth/verify-email — public, body { token }. This is what the link in
   the verification email actually calls; without it an account never flips to
   isEmailVerified and billing never activates. */
export const verifyEmail = ({ token }) =>
  apiPost('/api/v1/auth/verify-email', { token });

/* POST /auth/resend-verification — sits behind authMiddleware and reads
   req.user. It takes NO body and cannot look an account up by email, so it
   only works for a signed-in user. `auth: true` attaches the stored token. */
export const resendVerification = () =>
  apiPost('/api/v1/auth/resend-verification', undefined, { auth: true });

/* GET /organizations/validate-invitation?org=&token= — public. Returns
   { data: { organizationId, organizationName, token, expiresAt } }.
   Both query params are required; the emailed invite link carries them as
   ?token=…&email=…&org=…. */
export const validateInvitation = ({ org, token }) =>
  apiGet(
    `/api/v1/organizations/validate-invitation?org=${encodeURIComponent(org)}&token=${encodeURIComponent(token)}`,
  );

export const partnerLogin = async ({ email, password }) => {
  const r = await apiPost('/api/v1/auth/partner-login', { email: email.trim(), password });
  storeSession(r);
  return r;
};

/* -------------------------- contact & booking --------------------------- */

/* POST /api/v1/contact — one endpoint, two shapes, chosen by `type`.
   The server accepts ONLY type 'contact_us' or 'booking' (anything else is a
   400), runs verifyRecaptcha, and rejects the request if the hidden `website`
   honeypot has any content. */

export const submitContact = ({
  fullName,
  email,
  companyName,
  subject,
  message,
  inquiryType = 'General Inquiry',
  employeeSize,
  phone,
  recaptchaToken,
  website = '',
}) =>
  apiPost('/api/v1/contact', {
    type: 'contact_us',
    inquiryType,
    fullName: fullName.trim(),
    email: email.trim(),
    companyName: companyName.trim(),
    subject: subject.trim(),
    message: message.trim(),
    ...(employeeSize ? { employeeSize } : {}),
    ...(phone ? { phone: phone.trim() } : {}),
    userTimezone: browserTimezone(),
    recaptchaToken,
    website,
  });

/* NOTE: /api/v1/contact also accepts a `type: 'booking'` shape. Nothing here
   uses it, and neither does the live site — demos are booked through the
   Cal.com embed (see components/CalEmbed.jsx), which is the real scheduling
   system. A booking helper used to live here and was removed rather than left
   as dead code that looks like a working feature. */

export { ApiError };
